import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  LinearProgress,
  Alert,
  Grid,
  CircularProgress,
  Chip
} from '@mui/material';
import { ArrowBack as ArrowBackIcon, Refresh as RefreshIcon, Download as DownloadIcon } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import DashboardLayout from './DashboardLayout';
import * as api from '../utils/api';


const TaskProgress = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [task, setTask] = useState(null);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchTask = async () => {
    try { const response = await api.getAnalysisTask(id); setTask(response.data.data); } catch (err) { console.error('Error fetching task:', err); setError('Failed to load task'); }
  };
  const fetchStatus = async () => {
    try { const response = await api.getAnalysisTaskStatus(id); setStatus(response.data.data); } catch (err) { console.error('Error fetching task status:', err); }
  };

  useEffect(() => { const load = async () => { setLoading(true); await Promise.all([fetchTask(), fetchStatus()]); setLoading(false); }; load(); }, [id]);
  useEffect(() => {
    if (!status) return; const isActive = ['collecting_dialogs', 'processing'].includes(status.status);
    if (isActive) { const interval = setInterval(fetchStatus, 10000); return () => clearInterval(interval); }
  }, [status?.status]);

  const getProgress = () => { if (!status || !status.total_dialogs) return 0; return Math.round((status.processed_dialogs / status.total_dialogs) * 100); };
  const getStatusColor = (statusValue) => ({ 'collecting_dialogs': 'info', 'processing': 'warning', 'completed': 'success', 'failed': 'error' }[statusValue] || 'default');

  const handleRefresh = async () => { await Promise.all([fetchTask(), fetchStatus()]); };
    const handleDownloadNoReply = async () => {
      try {
        const response = await api.downloadNoReplyNumbers(id);
        const url = window.URL.createObjectURL(new Blob([response.data], { type: 'text/csv' }));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `no-reply-${id}.csv`);
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (e) { console.error('Failed to download no-reply numbers', e); }
    };
  const handleViewResults = () => navigate(`/campaign-analysis/${id}/results`);

  if (loading) {
    return (
      <DashboardLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
          <CircularProgress size={60} />
        </Box>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout>
        <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>
      </DashboardLayout>
    );
  }

  const progress = getProgress();

  return (
    <DashboardLayout>
      <Box sx={{ mb: 4 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/campaign-analysis')} sx={{ mb: 2 }}>Back to task list</Button>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h4" gutterBottom>{task?.name}</Typography>
            <Typography variant="body1" color="text.secondary">Tracking task progress</Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="outlined" startIcon={<RefreshIcon />} onClick={handleRefresh}>Refresh</Button>
            <Button variant="outlined" startIcon={<DownloadIcon />} onClick={handleDownloadNoReply}>Download no-reply</Button>
            {status?.status === 'completed' && (<Button variant="contained" onClick={handleViewResults}>View results</Button>)}
          </Box>
        </Box>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>Progress</Typography>
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="body1">{status?.processed_dialogs || 0} of {status?.total_dialogs || 0} dialogs processed</Typography>
                <Typography variant="h6" color="primary">{progress}%</Typography>
              </Box>
              <LinearProgress variant="determinate" value={progress} sx={{ height: 10, borderRadius: 5 }} />
            </Box>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
              <Chip label={status?.status || 'unknown'} color={getStatusColor(status?.status)} variant="outlined" />
              {status?.external_status && status.external_status !== status.status && (
                <Chip label={`External status: ${status.external_status}`} size="small" />
              )}
            </Box>
            <Box sx={{ p: 2, backgroundColor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary">
                {status?.status === 'collecting_dialogs' && 'Collecting dialogs from the campaign...'}
                {status?.status === 'processing' && 'Processing dialogs with AI...'}
                {status?.status === 'completed' && 'Task completed successfully'}
                {status?.status === 'failed' && 'An error occurred while executing the task'}
              </Typography>
            </Box>
            {status?.failed_dialogs > 0 && (
              <Alert severity="warning" sx={{ mt: 2 }}>{status.failed_dialogs} dialogs failed to process</Alert>
            )}
          </Paper>

          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>Task information</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">Campaign</Typography>
                <Typography variant="body1">{task?.Campaign?.name}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">Dialog duration</Typography>
                <Typography variant="body1">{task?.max_dialog_duration_hours} hours</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">Instructions</Typography>
                <Typography variant="body2" sx={{ fontFamily: 'monospace', backgroundColor: 'grey.100', p: 2, borderRadius: 1, whiteSpace: 'pre-wrap', maxHeight: 200, overflow: 'auto' }}>{task?.instructions}</Typography>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, mb: 3 }}>
            <Typography variant="h6" gutterBottom>Statistics</Typography>
            <Box sx={{ mt: 1, display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1 }}>
              <Box sx={{ p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1, textAlign: 'center' }}>
                <Typography variant="overline" color="text.secondary">Total contacts</Typography>
                <Typography variant="h6">{(status?.campaign_unanswered ?? 0)+(status?.campaign_answered ?? 0)}</Typography>
              </Box>
              <Box sx={{ p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1, textAlign: 'center' }}>
                <Typography variant="overline" color="text.secondary">Dialogs</Typography>
                <Typography variant="h6">{status?.campaign_answered ?? 0}</Typography>
              </Box>
              <Box sx={{ p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1, textAlign: 'center' }}>
                <Typography variant="overline" color="text.secondary">No reply</Typography>
                <Typography variant="h6" color="warning.main">{status?.campaign_unanswered ?? 0}</Typography>
              </Box>
            </Box>
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>Dialogs</Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1 }}>
                <Box sx={{ p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1, textAlign: 'center' }}>
                  <Typography variant="overline" color="text.secondary">Processed</Typography>
                  <Typography variant="h6" color="success.main">{status?.processed_dialogs ?? 0}</Typography>
                </Box>
                <Box sx={{ p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1, textAlign: 'center' }}>
                  <Typography variant="overline" color="text.secondary">Errors</Typography>
                  <Typography variant="h6" color="error.main">{status?.failed_dialogs ?? 0}</Typography>
                </Box>
              </Box>
            </Box>
          </Paper>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>Timeline</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box><Typography variant="body2" color="text.secondary">Created</Typography><Typography variant="body1">{task?.created_at ? new Date(task.created_at).toLocaleString() : 'N/A'}</Typography></Box>
              {task?.started_at && (<Box><Typography variant="body2" color="text.secondary">Started</Typography><Typography variant="body1">{new Date(task.started_at).toLocaleString()}</Typography></Box>)}
              {task?.completed_at && (<Box><Typography variant="body2" color="text.secondary">Completed</Typography><Typography variant="body1">{new Date(task.completed_at).toLocaleString()}</Typography></Box>)}
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </DashboardLayout>
  );
};

export default TaskProgress;
